/* eslint-disable camelcase -- API / persisted payload と同じ snake_case 拡張フィールド */
import type {
  RuleBasedErrorEntry,
  VariationSummaryItem,
  VariationSummaryPosition,
} from '@/lib/api/proofreading';

/** work_progress_data / シンク後に FE が付与する拡張フィールド */
export type RuleBasedErrorEntryStored = RuleBasedErrorEntry & {
  origin_start_line?: number;
  origin_start_word?: number;
  coord_invalid?: boolean;
};

export type VariationSummaryPositionStored = VariationSummaryPosition & {
  origin_start_line?: number;
  origin_start_word?: number;
  coord_invalid?: boolean;
};

export type VariationSummaryItemStored = Omit<VariationSummaryItem, 'variants'> & {
  variants: Array<
    Omit<VariationSummaryItem['variants'][number], 'position'> & {
      position?: VariationSummaryPositionStored[];
    }
  >;
};

export function getOriginStartLine(entry: RuleBasedErrorEntryStored): number {
  return entry.origin_start_line ?? entry.start_line;
}

export function getOriginStartWord(entry: RuleBasedErrorEntryStored): number {
  return entry.origin_start_word ?? entry.start_word;
}

export function getVariationOriginStartLine(pos: VariationSummaryPositionStored): number {
  return pos.origin_start_line ?? pos.start_line;
}

export function getVariationOriginStartWord(pos: VariationSummaryPositionStored): number {
  return pos.origin_start_word ?? pos.start_word;
}

export function stampOriginsOnResults(
  results: RuleBasedErrorEntry[],
): RuleBasedErrorEntryStored[] {
  return results.map((entry) => {
    const stored = entry as RuleBasedErrorEntryStored;
    return {
      ...entry,
      origin_start_line: stored.origin_start_line ?? entry.start_line,
      origin_start_word: stored.origin_start_word ?? entry.start_word,
      coord_invalid: stored.coord_invalid ?? false,
    };
  });
}

export function stampOriginsOnVariationSummary(
  items: VariationSummaryItem[],
): VariationSummaryItemStored[] {
  return items.map((item) => ({
    ...item,
    variants: item.variants.map((variant) => ({
      ...variant,
      position: (variant.position ?? []).map((pos) => {
        const stored = pos as VariationSummaryPositionStored;
        return {
          ...pos,
          origin_start_line: stored.origin_start_line ?? pos.start_line,
          origin_start_word: stored.origin_start_word ?? pos.start_word,
          coord_invalid: stored.coord_invalid ?? false,
        };
      }),
    })),
  }));
}

function buildResultPositionKey(entry: Pick<
  RuleBasedErrorEntry,
  'manuscript_id' | 'start_line' | 'start_word' | 'error_code'
>): string {
  return `${entry.manuscript_id}:${entry.start_line}:${entry.start_word}:${entry.error_code}`;
}

function buildVariationPositionKey(pos: Pick<
  VariationSummaryPosition,
  'manuscript_id' | 'start_line' | 'start_word'
>): string {
  return `${pos.manuscript_id}:${pos.start_line}:${pos.start_word}`;
}

export function mergeResultsPreservingOrigins(
  previous: RuleBasedErrorEntryStored[],
  next: RuleBasedErrorEntry[],
): RuleBasedErrorEntryStored[] {
  const usedPrevKeys = new Set<string>();

  return next.map((entry) => {
    const matchKey = buildResultPositionKey(entry);
    const prev = previous.find((p) => {
      const prevKey = buildResultPositionKey(p);
      return prevKey === matchKey && !usedPrevKeys.has(prevKey);
    });
    if (prev) {
      usedPrevKeys.add(buildResultPositionKey(prev));
    }
    return {
      ...entry,
      origin_start_line: prev?.origin_start_line ?? entry.start_line,
      origin_start_word: prev?.origin_start_word ?? entry.start_word,
      coord_invalid: false,
    };
  });
}

export function mergeVariationSummaryPreservingOrigins(
  previous: VariationSummaryItemStored[],
  next: VariationSummaryItem[],
): VariationSummaryItemStored[] {
  return next.map((item) => {
    const prevItem = previous.find(
      (p) => p.variation_type === item.variation_type && p.canonical === item.canonical,
    );

    return {
      ...item,
      variants: item.variants.map((variant) => {
        const prevVariant = prevItem?.variants.find((v) => v.text === variant.text);
        const usedPrevPositionKeys = new Set<string>();

        return {
          ...variant,
          position: (variant.position ?? []).map((pos) => {
            const matchKey = buildVariationPositionKey(pos);
            const prevPos = (prevVariant?.position ?? []).find((p) => {
              const prevKey = buildVariationPositionKey(p);
              return prevKey === matchKey && !usedPrevPositionKeys.has(prevKey);
            });
            if (prevPos) {
              usedPrevPositionKeys.add(buildVariationPositionKey(prevPos));
            }
            return {
              ...pos,
              origin_start_line: prevPos?.origin_start_line ?? pos.start_line,
              origin_start_word: prevPos?.origin_start_word ?? pos.start_word,
              coord_invalid: false,
            };
          }),
        };
      }),
    };
  });
}
